import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { XCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../../i18n";

// Elements by NexAir Logo (with light background - use blend mode)
const ELEMENTS_LOGO = 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/54bbp08l_IMG_1413.png';

// Elements Hero Video
const ELEMENTS_VIDEO = '/videos/nexair_show.mp4';

// Elements Gallery Videos
const ELEMENTS_GALLERY_VIDEOS = [
  { id: 'ev-1', url: '/videos/nexair_show.mp4', title: 'Show Nocturno Elements', type: 'video' },
  { id: 'ev-2', url: '/videos/nexair_show_2.mp4', title: 'Espectáculo de Luz y Agua', type: 'video' },
];

// Elements by NexAir Gallery Images - Combined Drones + Water Screen + Fire
const SKY_MEETS_WATER_GALLERY = [
  ...ELEMENTS_GALLERY_VIDEOS,
  {
    id: 'smw-1',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png',
    title: 'Boda con Drones y Water Screen',
    description: 'Elements by NexAir'
  },
  {
    id: 'smw-2',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zio3060d_9FD265EA-9199-4903-B7B9-E7CA7AB0CF82.png',
    title: 'Show Combinado',
    description: 'Drones y proyección acuática'
  },
  {
    id: 'smw-3',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/83vl4cac_A19EEFEF-D39C-4D93-9DD6-D0DD77F2074C.png',
    title: 'Paradise Resorts',
    description: 'Turismo y hospitalidad'
  },
  {
    id: 'smw-4',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/qsz97onz_94F891CD-7FDF-4D0C-A27A-92E6AD27A26B.png',
    title: 'Tigre Espectacular',
    description: 'Activación de marca'
  },
  {
    id: 'smw-5',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/gt8djmb8_3EFC8447-B984-4D7B-9C85-1271651DC812.png',
    title: 'Castle Hotels',
    description: 'Experiencia premium'
  },
  {
    id: 'smw-6',
    url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/xs24fn5p_0F8C6FC4-D615-4552-9E9A-CFC630B64A14.png',
    title: 'Luminara Residences',
    description: 'Lanzamiento inmobiliario'
  },
];

const SkyMeetsWaterSection = () => {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="sky-meets-water" 
      ref={sectionRef}
      className="relative w-full bg-[#020408] overflow-hidden scroll-mt-20"
      data-testid="elements-by-nexair-section"
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="w-full h-full object-cover opacity-40"
        >
          <source src={ELEMENTS_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#020408] via-transparent to-[#020408]" />
      </div>

      {/* Content - All in one screen */}
      <div className="relative z-10 w-full px-6 md:px-12 pt-4 pb-12 md:pt-6 md:pb-16">
        <div className="max-w-6xl mx-auto text-center">
          {/* Elements by NexAir Logo */}
          <div 
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <img 
              src={ELEMENTS_LOGO} 
              alt="Elements by NexAir" 
              className="h-72 md:h-96 lg:h-[28rem] w-auto mx-auto object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]"
              style={{ mixBlendMode: 'screen' }}
            />
          </div>

          {/* Subheadline */}
          <p 
            className={`text-base md:text-lg text-white/80 font-light tracking-wide mb-2 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {t('skyMeetsWater.subheadline')}
          </p>

          {/* Decorative line */}
          <div 
            className={`w-16 md:w-24 h-[1px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent mx-auto mb-4 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          />

          {/* Description */}
          <p 
            className={`text-sm md:text-base text-gray-300 leading-relaxed max-w-2xl mx-auto mb-8 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {t('skyMeetsWater.description')}
          </p>

          {/* Gallery Grid - 3 columns x 2 rows */}
          <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {SKY_MEETS_WATER_GALLERY.map((item) => (
              <div 
                key={item.id}
                className="group relative aspect-video rounded-xl overflow-hidden cursor-pointer border border-white/10 hover:border-cyan-500/50 transition-all"
                onClick={() => setLightboxImage(item)}
              >
                {item.type === 'video' ? (
                  <video 
                    src={item.url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <img 
                    src={item.url} 
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                  <h4 className="text-white text-sm font-semibold">{item.title}</h4>
                  <p className="text-gray-300 text-xs">{item.description}</p>
                </div>
                {/* Video indicator */}
                {item.type === 'video' && (
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                    Video
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <a 
              href="#contacto"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold transition-all hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]"
            >
              {language === 'es' ? 'Cotizar Elements' : 'Quote Elements'}
            </a>
            <Link 
              to="/elements"
              className="inline-flex items-center gap-2 px-6 py-3 text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              {language === 'es' ? 'Ver más' : 'Learn more'}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox for images and videos */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightboxImage.type === 'video' ? (
              <video 
                src={lightboxImage.url}
                autoPlay
                controls
                playsInline
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <img 
                src={lightboxImage.url} 
                alt={lightboxImage.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
            )}
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
              <p className="text-gray-400">{lightboxImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SkyMeetsWaterSection;
